import {
  Card,
  DashPlaceholder,
  Flex,
  Image,
  TooltipWrapper,
  Badge,
  AddressLabel,
  useScreenResolution,
} from 'dash-ui';
import { AddressModel, BrokerModel, ProfileModel } from 'dash-common';
import { useTranslation } from 'react-i18next';
import {
  GetBrokerByIdQuery,
  PortfoliosByPublicFolderListItemFragment,
  Property,
} from 'gql/generated.graphql';
import { useTheme } from 'styled-components';

import { getPreviewPortfolioPropertyLink } from 'common/helpers/portfolio';
import { formatPropertyPrice } from 'pages/portfolios/PortfolioDetailsPage/components/PortfolioDetailView/components/PortfolioPropertiesListView/components/PortfolioPropertyCardItem/PortfolioPropertyCardItem.helpers';
import PortfolioLockMask, {
  RelativeContainer,
} from 'containers/portfolio/PortfolioLockMask/PortfolioLockMask';
import { BadgesWrapper } from './PropertyCardItem.styles';
import VerifiedBadge from 'common/components/VerifiedBadge/VerifiedBadge';
import { StyledText } from 'containers/properties/PropertyCard/PropertyCard.styles';
import PropertyUnitsRow from 'containers/properties/PropertyCard/components/PropertyUnitsRow';
import ProjectStatusRow from 'containers/properties/PropertyCard/components/ProjectStatusRow';

const PortfolioCardItem = ({
  portfolioItem,
  broker,
}: {
  portfolioItem: PortfoliosByPublicFolderListItemFragment;
  broker?: GetBrokerByIdQuery['getBrokerById'];
}) => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const property = portfolioItem.property;
  const file = property?.mainPropertyMediaFile?.files[0];
  const { formattedPrice, ...priceRange } = formatPropertyPrice(
    property?.priceRange
  );
  const {
    devices: { isTablet, isMobile },
  } = useScreenResolution();
  return (
    <Card
      flexDirection="column"
      onClick={() => {
        if (broker) {
          window?.open(
            getPreviewPortfolioPropertyLink(
              { ...portfolioItem, name: portfolioItem.property?.name },
              broker as BrokerModel,
              broker.profile as ProfileModel
            ),
            '_blank'
          );
        }
      }}
    >
      <BadgesWrapper p={3}>
        {property?.marketArea && (
          <Badge
            title={property.marketArea.name}
            bgColor={theme.colors.setOpacity(theme.colors.b50, 70)}
            titleColor={theme.colors.white}
            semibold={false}
            py={2}
            mr={2}
            md
            sm={isTablet || isMobile}
            square={true}
          />
        )}
        {property?.isMarkedAsVerified && <VerifiedBadge />}
      </BadgesWrapper>
      <RelativeContainer h={270} w="100%">
        {file ? (
          <Image
            src={file.thumbnailFile?.publicUrl || ''}
            size="100%"
            borderRadius={0}
          />
        ) : (
          <DashPlaceholder
            variation="secondary"
            w="100%"
            h="100%"
            borderRadius={0}
          />
        )}
        <PortfolioLockMask w="100%" h="100%" portfolio={portfolioItem} />
      </RelativeContainer>
      <Flex
        px={[4, 3, 4, 4]}
        py={2}
        flexDirection="column"
        justifyContent="space-between"
        maxWidth="100%"
      >
        <Flex w="100%" flexDirection="column">
          <TooltipWrapper content={property?.name}>
            <StyledText
              clamp={1}
              lg={!isTablet && !isMobile}
              semibold
              color={theme.colors.n90}
            >
              {property?.name}
            </StyledText>
          </TooltipWrapper>
          <PropertyUnitsRow
            property={property as Property}
            smallScale={isMobile}
          />
        </Flex>
        <Flex flexDirection="column" w="100%" mt={4} mb={2}>
          <Flex w="100%" flex={1}>
            <AddressLabel
              sm={isMobile}
              color={theme.colors.n80}
              address={property?.location.address as AddressModel}
            />
          </Flex>
          <Flex flex={1} w="100%">
            <ProjectStatusRow
              property={property as Property}
              smallScale={isMobile}
            />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

export default PortfolioCardItem;
